export type OpenSCADErrorSeverity = 'error' | 'warning';

export type OpenSCADErrorCategory =
  | 'Syntax Error'
  | 'Geometry Error'
  | 'Missing File'
  | 'Compilation Error'
  | 'Warning';

export interface OpenSCADErrorEntry {
  severity: OpenSCADErrorSeverity;
  line?: number;
  column?: number;
  message: string;
  raw: string;
}

export interface ParsedOpenSCADErrors {
  category: OpenSCADErrorCategory;
  summary: string;
  entries: OpenSCADErrorEntry[];
}

// Matches: ERROR: Parser error in file "input.scad", line 5: syntax error
// Matches: WARNING: some message in file "input.scad", line 3
// Matches: ...line 12: message (without file reference)
const LINE_PATTERN = /line (\d+)(?:,?\s*column\s*(\d+))?(?::\s*(.*))?/i;

// Strip common OpenSCAD prefixes to get a readable message
function stripPrefix(line: string): string {
  return line
    .replace(/^ERROR:\s*/i, '')
    .replace(/^WARNING:\s*/i, '')
    .trim();
}

function extractLineAndMessage(raw: string): {
  line?: number;
  column?: number;
  message: string;
} {
  const match = raw.match(LINE_PATTERN);
  if (match) {
    const line = parseInt(match[1], 10);
    const column = match[2] ? parseInt(match[2], 10) : undefined;
    // Use the captured message after the line ref, or fall back to stripped full line
    const message = match[3]?.trim() || stripPrefix(raw);
    return { line, column, message };
  }
  return { message: stripPrefix(raw) };
}

function detectCategory(stdErr: string[]): OpenSCADErrorCategory {
  for (const line of stdErr) {
    const lower = line.toLowerCase();
    if (lower.includes('parser error') || lower.includes('syntax error')) {
      return 'Syntax Error';
    }
    if (
      lower.includes('cgal error') ||
      lower.includes('cgal exception') ||
      lower.includes('mesh is not closed') ||
      lower.includes('manifold') ||
      lower.includes('boolean operation') ||
      lower.includes('is not a 3d object')
    ) {
      return 'Geometry Error';
    }
    if (
      lower.includes("can't open") ||
      lower.includes('cannot open') ||
      lower.includes('no such file') ||
      lower.includes('failed to open') ||
      lower.includes('use <') ||
      lower.includes('include <')
    ) {
      return 'Missing File';
    }
  }

  // If all lines are warnings, call it a Warning
  const errorLines = stdErr.filter((l) => /^ERROR:/i.test(l));
  if (errorLines.length === 0 && stdErr.some((l) => /^WARNING:/i.test(l))) {
    return 'Warning';
  }

  return 'Compilation Error';
}

export function parseOpenSCADErrors(
  stdErr: string[],
  stdOut: string[] = [],
): ParsedOpenSCADErrors {
  const entries: OpenSCADErrorEntry[] = [];

  // Parse stderr lines into structured entries
  for (const raw of stdErr) {
    const isError = /^ERROR:/i.test(raw);
    const isWarning = /^WARNING:/i.test(raw);

    if (!isError && !isWarning) continue;

    const severity: OpenSCADErrorSeverity = isWarning ? 'warning' : 'error';
    const { line, column, message } = extractLineAndMessage(raw);

    entries.push({ severity, line, column, message, raw });
  }

  // Also surface "Top level object is empty" from stdout if no other errors found
  if (entries.length === 0) {
    for (const raw of [...stdErr, ...stdOut]) {
      if (
        raw.toLowerCase().includes('top level object is empty') ||
        raw.toLowerCase().includes('current top level object is not')
      ) {
        entries.push({ severity: 'error', message: raw.trim(), raw });
      }
    }
  }

  const category = detectCategory(stdErr);

  // Build a human-readable summary from the first error entry with a line number,
  // or fall back to the first error entry, or a generic message.
  let summary: string;
  const firstWithLine = entries.find(
    (e) => e.severity === 'error' && e.line !== undefined,
  );
  const firstError = entries.find((e) => e.severity === 'error');
  const target = firstWithLine ?? firstError ?? entries[0];

  if (target) {
    summary =
      target.line !== undefined
        ? `Line ${target.line}: ${target.message}`
        : target.message;
  } else {
    // Fallback: use the raw first non-empty stderr line
    const firstRaw = stdErr.find((l) => l.trim().length > 0);
    summary = firstRaw ? stripPrefix(firstRaw) : 'Unknown compilation error';
  }

  return { category, summary, entries };
}
